import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Save, Play, RefreshCw } from 'lucide-react';
import { useVoiceConfig, useVoiceAgents, useAgentProfiles } from '../../hooks/useVoiceAPI';
import apiService from '../../services/apiService';
import { useBranding } from '../../context/BrandingContext';
import { useUiLanguage } from '../../context/UiLanguageContext';

const VoiceConfigPanel = ({ profileId }) => {
    const { t } = useUiLanguage();
    const brandingContext = useBranding();
    const isFitenko = Boolean(
        brandingContext?.isFitenko ||
        brandingContext?.branding?.instance_name === 'fitenko' ||
        (typeof window !== 'undefined' && (
            window.location.hostname.includes('fitenko') ||
            localStorage.getItem('tenant_id') === 'fitenko' ||
            localStorage.getItem('lastTenant') === 'fitenko'
        ))
    );

    const { config, loading, updateConfig, refetch: refetchConfig } = useVoiceConfig(profileId);
    const { agents } = useVoiceAgents();

    // [PROTOCOL v5.1] Multilingual Tab State
    const [activeLang, setActiveLang] = useState('en-US');
    const [saving, setSaving] = useState(false);

    // Live assistant status (truth about production Vapi assistant and code-compiled prompt)
    const [liveAssistantInfo, setLiveAssistantInfo] = useState({
        assistant_id: 'fb363e6f-ece2-48fb-9fc2-15a4d041c6fb',
        assistant_short_id: 'fb363e6f…',
        prompt_length: 33583,
        prompt_hash: 'b5a8241ca783b570e1270673d0993d42102958eb90834ad37fa323b9f36cde07',
        is_code_driven: true,
    });

    useEffect(() => {
        let mounted = true;
        apiService.makeRequest('/voice/live-assistant-info')
            .then((data) => {
                if (mounted && data) {
                    setLiveAssistantInfo(data);
                }
            })
            .catch(() => {});
        return () => { mounted = false; };
    }, []);

    // --- Синхронизация с Vapi (quick 260901-q2w) ---
    // Промпт, который слышит звонящий, живёт в Vapi. Эта страница до правки писала
    // во ВТОРУЮ, мёртвую копию и безусловно говорила «сохранено».
    const [pulling, setPulling] = useState(false);
    // 🔴 Отпечаток, который ВИДЕЛ БРАУЗЕР в момент загрузки текста в форму.
    // Он закрывает щель, которую сверка «база ↔ Vapi» не закрывает: вторая
    // открытая вкладка переживает чужой pull, хэши базы и Vapi сходятся,
    // и старый текст из неё уехал бы поверх живого.
    const [baseHash, setBaseHash] = useState(null);
    // Вердикт держится на странице до следующего действия — не alert, который
    // закрывается без следа.
    const [syncVerdict, setSyncVerdict] = useState(null);

    // [INIT] Default State to avoid uncontrolled inputs or early null access
    const [formData, setFormData] = useState({
        twilioPhoneNumber: '',
        provider: 'vapi', // Vapi.ai (Production)
        sttProvider: 'deepgram',
        ttsProvider: 'deepgram',
        llmModel: 'gemini-2.5-flash',
        voiceProvider: 'elevenlabs',
        voiceId: '',
        systemPromptTemplate: '',
        firstMessage: '',
        isActive: true,
        voiceAgentId: null,
        deepgramModel: 'nova-3',
        enabledLanguages: { en: true, ru: true, es: true },
        guardrails: {
            enableTools: true,
            transferOnAngry: false,
            requireConsent: true,
            maxCallDuration: 600,
        }
    });

    // [FIX] Dependent on formData ID (Dropdown) not just Config (Database)
    // This ensures when user switches roles, we fetch the new role's profiles immediately.
    const agentId = formData.voiceAgentId || config?.voice_agent_id || null;
    const { profiles, refetch: refetchProfiles } = useAgentProfiles(agentId);

    // Профиль активного языка: коды на фронте — en-US / ru-RU / es-ES.
    const activeProfile = profiles.find(p => p.language_code === activeLang) || null;
    const isLinkedToVapi = Boolean(activeProfile && activeProfile.vapi_assistant_id);

    // Отпечаток запоминается ровно в тот момент, когда текст лёг в форму.
    useEffect(() => {
        setBaseHash((activeProfile && activeProfile.vapi_prompt_hash) || null);
    }, [activeProfile]);

    // Вердикт снимается при смене языка, но НЕ при перечитывании профилей после
    // сохранения — иначе баннер исчез бы ровно тогда, когда его надо прочитать.
    useEffect(() => {
        setSyncVerdict(null);
    }, [activeLang]);

    // 1. Load Initial Config (Routing & English Defaults)
    useEffect(() => {
        if (config) {
            setFormData(prev => {
                // Only update if we haven't touched the form yet or if config ID changed
                if (prev.voiceAgentId && prev.voiceAgentId !== config.voice_agent_id) return prev;

                return {
                    ...prev,
                    twilioPhoneNumber: config.twilio_phone_number || config.twilioPhoneNumber || '',
                    provider: config.provider || 'vapi',
                    sttProvider: config.stt_provider || config.sttProvider || 'deepgram',
                    ttsProvider: config.tts_provider || config.ttsProvider || 'deepgram',
                    llmModel: config.llm_model || config.llmModel || 'gemini-2.5-flash',
                    voiceProvider: config.voice_provider || config.voiceProvider || 'elevenlabs',
                    voiceId: config.voice_id || config.voiceId || '',
                    systemPromptTemplate: config.system_prompt_template || config.systemPromptTemplate || '',
                    firstMessage: config.first_message || config.firstMessage || '',
                    isActive: config.is_active !== undefined ? config.is_active : (config.isActive !== undefined ? config.isActive : true),
                    voiceAgentId: config.voice_agent_id || config.voiceAgentId || null,
                    guardrails: config.guardrails_config || config.guardrailsConfig || {
                        enableTools: true,
                        transferOnAngry: false,
                        requireConsent: true,
                        maxCallDuration: 600,
                    },
                    deepgramModel: config.deepgram_model || config.deepgramModel || 'nova-3',
                    enabledLanguages: config.enabled_languages || config.enabledLanguages || { en: true, ru: true, es: true },
                };
            });
        }
    }, [config]);

    // 2. [FIX] Auto-select Casey (FRONT_DESK) if no agent is selected yet
    useEffect(() => {
        if (!formData.voiceAgentId && agents.length > 0) {
            const casey = agents.find(a => a.internal_name === 'Casey' || a.role_type === 'FRONT_DESK');
            if (casey) {
                setFormData(prev => ({ ...prev, voiceAgentId: casey.id }));
            }
        }
    }, [agents, formData.voiceAgentId]);

    // 3. Загрузка промптов и голоса в зависимости от языка и выбранного агента
    useEffect(() => {
        if (!formData.voiceAgentId) return;

        if (activeLang === 'en-US') {
            // А. Пробуем загрузить профиль языка
            const enProfile = profiles.find(p => p.language_code === 'en-US');
            if (enProfile) {
                setFormData(prev => ({
                    ...prev,
                    systemPromptTemplate: enProfile.system_prompt || '',
                    firstMessage: enProfile.first_message || '',
                    voiceId: enProfile.voice_id || prev.voiceId,
                    voiceProvider: enProfile.voice_provider || prev.voiceProvider,
                }));
                return;
            }

            // Б. Fallback на самого агента (Legacy fields)
            if (agents.length > 0) {
                const agent = agents.find(a => a.id === formData.voiceAgentId);
                if (agent) {
                    setFormData(prev => ({
                        ...prev,
                        systemPromptTemplate: agent.system_prompt || '',
                        firstMessage: agent.first_message || prev.firstMessage,
                    }));
                }
            }
        } else {
            // Иностранные языки - только через профиль
            const profile = profiles.find(p => p.language_code === activeLang);
            if (profile) {
                setFormData(prev => ({
                    ...prev,
                    systemPromptTemplate: profile.system_prompt || '',
                    firstMessage: profile.first_message || '',
                    voiceId: profile.voice_id || '',
                    voiceProvider: profile.voice_provider || 'elevenlabs',
                }));
            } else {
                setFormData(prev => ({
                    ...prev,
                    systemPromptTemplate: '',
                    firstMessage: '',
                    voiceId: '',
                    voiceProvider: 'elevenlabs',
                }));
            }
        }
    }, [activeLang, profiles, formData.voiceAgentId, agents]);

    // 🔴 Обновить текст из Vapi — ЕДИНСТВЕННЫЙ способ увидеть на странице то,
    // что реально исполняется на звонке.
    const handlePullFromVapi = async () => {
        if (!formData.voiceAgentId || !isLinkedToVapi) return;
        setPulling(true);
        setSyncVerdict(null);
        try {
            const res = await apiService.makeRequest(
                `/voice/agents/${formData.voiceAgentId}/profiles/${activeLang}/pull-from-vapi`,
                { method: 'POST' }
            );
            await refetchProfiles();
            setSyncVerdict({
                tone: 'ok',
                text: t('voice.config.pull_success', { chars: res.chars, lang: activeLang }),
            });
        } catch (error) {
            setSyncVerdict({
                tone: 'error',
                text: t('voice.config.pull_error', { error: error.message }),
            });
        } finally {
            setPulling(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setSyncVerdict(null);

        try {
            // A. Save Profile (if agent selected)
            let vapiSync = null;
            if (formData.voiceAgentId && formData.voiceAgentId !== "custom") {
                const profilePayload = {
                    language_code: activeLang,
                    voice_id: formData.voiceId,
                    system_prompt: formData.systemPromptTemplate,
                    first_message: formData.firstMessage,
                    voice_provider: formData.voiceProvider,
                    tools_config: []
                };

                const query = baseHash ? `?base_hash=${encodeURIComponent(baseHash)}` : '';

                const saveRes = await apiService.makeRequest(
                    `/voice/agents/${formData.voiceAgentId}/profiles/${activeLang}${query}`,
                    {
                        method: 'PUT',
                        body: JSON.stringify(profilePayload),
                    }
                );
                vapiSync = saveRes && saveRes.vapi_sync ? saveRes.vapi_sync : null;
            }

            // B. Save Config (Routing)
            const routingPayload = {
                profile_id: profileId === "default" ? null : profileId,
                twilio_phone_number: formData.twilioPhoneNumber,
                agent_role: agents.find(a => a.id === formData.voiceAgentId)?.role_type || "FRONT_DESK",
                voice_agent_id: formData.voiceAgentId === "custom" ? null : formData.voiceAgentId,
                default_language_code: "en-US",
                is_active: formData.isActive,
                deepgram_model: formData.deepgramModel || "nova-3",
                provider: formData.provider,
                stt_provider: formData.sttProvider,
                tts_provider: formData.ttsProvider,
                llm_model: formData.llmModel,
                voice_provider: formData.voiceProvider,
                voice_id: formData.voiceId,
                enabled_languages: formData.enabledLanguages,
            };

            await updateConfig(routingPayload);

            if (vapiSync && vapiSync.status === 'pushed') {
                const notPushed = (vapiSync.not_pushed_fields || []).join(', ');
                const restText = notPushed ? t('voice.config.sync_not_pushed', { fields: notPushed }) : '';
                setSyncVerdict({
                    tone: 'ok',
                    text: t('voice.config.sync_pushed', { chars: vapiSync.chars, lang: activeLang, rest: restText }),
                });
            } else if (vapiSync && vapiSync.status === 'skipped') {
                setSyncVerdict({
                    tone: 'warn',
                    text: t('voice.config.sync_skipped'),
                });
            } else {
                setSyncVerdict({
                    tone: 'warn',
                    text: t('voice.config.sync_routing_saved'),
                });
            }
            refetchProfiles();
            refetchConfig();

        } catch (error) {
            console.error(error);
            const reason = error.message;
            if (reason && reason.indexOf('VAPI_PATCH_APPLIED_VERIFY_FAILED') !== -1) {
                setSyncVerdict({
                    tone: 'error',
                    text: t('voice.config.sync_error_verify_failed', { reason: reason || '' }),
                });
            } else {
                setSyncVerdict({
                    tone: 'error',
                    text: t('voice.config.sync_error_not_pushed', { reason: reason || '' }),
                });
            }
        } finally {
            setSaving(false);
        }
    };

    const handleTestCall = async () => {
        alert('Test call initiated (Mock)');
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '16rem' }}>
                <div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border)', borderTop: '3px solid var(--accent)', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <Tabs defaultValue="basic" style={{ width: '100%' }}>
                <TabsList 
                    className="grid grid-cols-1 sm:grid-cols-3 gap-1 p-1 w-full"
                    style={{ 
                        height: 'auto', 
                        minHeight: '2.5rem',
                        backgroundColor: 'var(--surface-sunken)',
                        border: '1px solid var(--border)',
                        borderRadius: '0.5rem'
                    }}
                >
                    <TabsTrigger value="basic" className="py-2 text-xs sm:text-sm font-medium">Basic Settings</TabsTrigger>
                    <TabsTrigger value="voice" className="py-2 text-xs sm:text-sm font-medium">Voice & Behavior</TabsTrigger>
                    <TabsTrigger value="guardrails" className="py-2 text-xs sm:text-sm font-medium">Guardrails</TabsTrigger>
                </TabsList>

                <TabsContent value="basic" style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <Card style={{ padding: '1.5rem' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                            {/* [PROTOCOL v5.1] AI Role Selector */}
                            <div style={{
                                padding: '1rem',
                                backgroundColor: 'var(--accent-soft)',
                                border: '1px solid var(--accent-border)',
                                borderRadius: '0.5rem'
                            }}>
                                <Label htmlFor="voiceAgent" style={{ color: 'var(--text)', fontWeight: '600' }}>AI Role (Personality & Skills)</Label>
                                <Select
                                    value={formData.voiceAgentId || "custom"}
                                    onValueChange={(value) => {
                                        const newVal = value === "custom" ? null : value;
                                        setFormData({ ...formData, voiceAgentId: newVal });
                                    }}
                                >
                                    <SelectTrigger style={{ backgroundColor: 'var(--surface-raised)' }}>
                                        {/* [FIX] Manually render label because SelectValue displays the raw ID */}
                                        <span>
                                            {(() => {
                                                if (!formData.voiceAgentId || formData.voiceAgentId === "custom") return "Manual Setup (Custom)";
                                                const selected = agents.find(a => a.id === formData.voiceAgentId);
                                                return selected ? `${selected.internal_name} (${selected.role_type})` : "Select a role...";
                                            })()}
                                        </span>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="custom">Manual Setup (Custom)</SelectItem>
                                        {agents.map(agent => (
                                            <SelectItem key={agent.id} value={agent.id}>
                                                {agent.internal_name} ({agent.role_type})
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <Label htmlFor="phoneNumber">Twilio Number</Label>
                                <Input
                                    id="phoneNumber"
                                    placeholder="+1-XXX-XXX-XXXX"
                                    value={formData.twilioPhoneNumber}
                                    onChange={(e) =>
                                        setFormData({ ...formData, twilioPhoneNumber: e.target.value })
                                    }
                                />
                            </div>

                            <div>
                                <Label htmlFor="firstMessage">First Message (Greeting)</Label>
                                {/* 🔴 PATCH шлёт только model; firstMessage лежит на верхнем
                                    уровне ассистента Vapi и этим путём в эфир не уезжает. */}
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                                    {t('voice.config.first_message_note')}
                                </div>
                                <Textarea
                                    id="firstMessage"
                                    placeholder="Hello, thanks for calling our firm..."
                                    value={formData.firstMessage}
                                    onChange={(e) =>
                                        setFormData({ ...formData, firstMessage: e.target.value })
                                    }
                                    rows={3}
                                />
                            </div>

                            {/* [NEW] Enabled Languages Toggles */}
                            <div style={{ padding: '1rem', backgroundColor: 'var(--surface-sunken)', border: '1px solid var(--border)', borderRadius: '0.5rem' }}>
                                <Label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: 600 }}>Enabled Languages</Label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
                                            🇺🇸 <span>English</span>
                                            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginLeft: '0.25rem' }}>(always on)</span>
                                        </span>
                                        <Switch
                                            checked={true}
                                            disabled={true}
                                            style={{ opacity: 0.6 }}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
                                            🇷🇺 <span>Russian</span>
                                        </span>
                                        <Switch
                                            checked={formData.enabledLanguages?.ru ?? true}
                                            onCheckedChange={(checked) =>
                                                setFormData({ ...formData, enabledLanguages: { ...formData.enabledLanguages, ru: checked } })
                                            }
                                        />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
                                            🇪🇸 <span>Spanish</span>
                                        </span>
                                        <Switch
                                            checked={formData.enabledLanguages?.es ?? true}
                                            onCheckedChange={(checked) =>
                                                setFormData({ ...formData, enabledLanguages: { ...formData.enabledLanguages, es: checked } })
                                            }
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* [NEW] Language Tabs */}
                            <div>
                                <Label>Language & Prompt</Label>
                                <Tabs value={activeLang} onValueChange={setActiveLang} style={{ marginTop: '0.5rem' }}>
                                    <TabsList className="grid grid-cols-1 sm:grid-cols-3 gap-1 p-1 w-full" style={{ height: 'auto', minHeight: '2.5rem' }}>
                                        <TabsTrigger value="en-US" className="py-2 text-xs sm:text-sm font-medium">🇺🇸 English (Default)</TabsTrigger>
                                        <TabsTrigger
                                            value="ru-RU"
                                            disabled={!(formData.enabledLanguages?.ru ?? true)}
                                            style={!(formData.enabledLanguages?.ru ?? true) ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
                                            className="py-2 text-xs sm:text-sm font-medium"
                                        >
                                            🇷🇺 Russian
                                        </TabsTrigger>
                                        <TabsTrigger
                                            value="es-ES"
                                            disabled={!(formData.enabledLanguages?.es ?? true)}
                                            style={!(formData.enabledLanguages?.es ?? true) ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
                                            className="py-2 text-xs sm:text-sm font-medium"
                                        >
                                            🇪🇸 Spanish
                                        </TabsTrigger>
                                    </TabsList>
                                </Tabs>
                            </div>

                            {/* Truthful Vapi Production Status */}
                            <div style={{ padding: '0.75rem', background: 'var(--surface-sunken)', border: '1px solid var(--border)', borderRadius: '0.5rem', fontSize: '0.8rem', color: 'var(--text)' }}>
                                <div style={{ fontWeight: 600, marginBottom: '0.375rem' }}>
                                    {t('voice.config.vapi_status_title')}
                                </div>
                                <div style={{ marginBottom: '0.375rem', lineHeight: '1.4' }}>
                                    {t('voice.config.vapi_explanation', { id: liveAssistantInfo.assistant_short_id || 'fb363e6f…' })}
                                </div>
                                <div style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.375rem' }}>
                                    {t('voice.config.vapi_assistant_active', { id: liveAssistantInfo.assistant_id || 'fb363e6f-ece2-48fb-9fc2-15a4d041c6fb' })}
                                </div>
                                <div style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                                    {t('voice.config.vapi_live_prompt_info', { chars: liveAssistantInfo.prompt_length, hash: (liveAssistantInfo.prompt_hash || '').slice(0, 16) + '…' })}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.375rem' }}>
                                    {t('voice.config.vapi_prompt_origin_note')}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                    {t('voice.config.vapi_form_length', { chars: (formData.systemPromptTemplate || '').length })}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                    {t('voice.config.vapi_last_sync', { date: activeProfile && activeProfile.vapi_synced_at ? new Date(activeProfile.vapi_synced_at).toLocaleString() : t('voice.config.vapi_never') })}
                                </div>
                                <div style={{ marginTop: '0.5rem' }}>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        disabled={!isLinkedToVapi || pulling}
                                        onClick={handlePullFromVapi}
                                    >
                                        {pulling ? t('voice.config.vapi_pulling') : t('voice.config.vapi_pull_btn')}
                                    </Button>
                                </div>
                            </div>

                            {syncVerdict && (
                                <div
                                    style={{
                                        padding: '0.75rem',
                                        borderRadius: '0.5rem',
                                        fontSize: '0.8rem',
                                        border: '1px solid',
                                        borderColor: syncVerdict.tone === 'ok' ? 'var(--success-border)'
                                            : (syncVerdict.tone === 'warn' ? 'var(--warning-border)' : 'var(--danger-border)'),
                                        background: syncVerdict.tone === 'ok' ? 'var(--success-soft)'
                                            : (syncVerdict.tone === 'warn' ? 'var(--warning-soft)' : 'var(--danger-soft)'),
                                        color: syncVerdict.tone === 'ok' ? 'var(--success)'
                                            : (syncVerdict.tone === 'warn' ? 'var(--warning)' : 'var(--danger)'),
                                        whiteSpace: 'pre-wrap',
                                    }}
                                >
                                    {syncVerdict.text}
                                </div>
                            )}

                            <div>
                                <Label htmlFor="systemPrompt">System Prompt</Label>
                                <div style={{ fontSize: '0.75rem', color: 'var(--warning)', marginTop: '0.25rem' }}>
                                    {t('voice.config.prompt_warning_unlinked', { id: liveAssistantInfo.assistant_short_id || 'fb363e6f…' })}
                                </div>
                                <Textarea
                                    id="systemPrompt"
                                    placeholder={`System prompt for ${activeLang}...`}
                                    value={formData.systemPromptTemplate}
                                    onChange={(e) =>
                                        setFormData({ ...formData, systemPromptTemplate: e.target.value })
                                    }
                                    rows={15}
                                    style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                                />
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div>
                                    <Label htmlFor="isActive">Agent Active</Label>
                                </div>
                                <Switch
                                    id="isActive"
                                    checked={formData.isActive}
                                    onCheckedChange={(checked) =>
                                        setFormData({ ...formData, isActive: checked })
                                    }
                                />
                            </div>
                        </div>
                    </Card>
                </TabsContent>

                <TabsContent value="voice" style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <Card style={{ padding: '1.5rem' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            <div style={{ padding: '1rem', background: 'var(--surface-sunken)', borderRadius: '0.5rem' }}>
                                <Label style={{ marginBottom: '0.5rem', display: 'block' }}>{t('voice.config.provider_label')}</Label>
                                <div style={{ display: 'flex', gap: '1rem' }}>
                                    <Button
                                        type="button"
                                        variant="default"
                                        disabled
                                        style={{ flex: 1, opacity: 1, cursor: 'default' }}
                                    >
                                        {t('voice.config.provider_vapi_prod')}
                                    </Button>
                                </div>
                            </div>

                            {formData.provider === 'vapi' ? (
                                <div>
                                    <Label htmlFor="voiceProvider">Vapi Voice Provider</Label>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                                        {t('voice.config.voice_provider_note')}
                                    </div>
                                    <Select
                                        value={formData.voiceProvider}
                                        onValueChange={(value) =>
                                            setFormData({ ...formData, voiceProvider: value })
                                        }
                                    >
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="elevenlabs">ElevenLabs</SelectItem>
                                            <SelectItem value="openai">OpenAI</SelectItem>
                                            <SelectItem value="azure">Azure</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <div style={{ marginTop: '1rem' }}>
                                        <Label htmlFor="voiceId">Voice ID</Label>
                                        <Input
                                            id="voiceId"
                                            value={formData.voiceId}
                                            onChange={(e) => setFormData({ ...formData, voiceId: e.target.value })}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                        <div>
                                            <Label>STT Provider</Label>
                                            <Select
                                                value={formData.sttProvider}
                                                onValueChange={(value) => setFormData({ ...formData, sttProvider: value })}
                                            >
                                                <SelectTrigger><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="deepgram">Deepgram</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label>TTS Provider</Label>
                                            <Select
                                                value={formData.ttsProvider}
                                                onValueChange={(value) => setFormData({ ...formData, ttsProvider: value })}
                                            >
                                                <SelectTrigger><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="elevenlabs">ElevenLabs</SelectItem>
                                                    <SelectItem value="deepgram">Deepgram</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    <div>
                                        <Label>LLM Model</Label>
                                        <Select
                                            value={formData.llmModel}
                                            onValueChange={(value) => setFormData({ ...formData, llmModel: value })}
                                        >
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="gemini-2.5-flash">Gemini 2.5 Flash</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label htmlFor="voiceId">Voice ID</Label>
                                        <Input
                                            id="voiceId"
                                            value={formData.voiceId}
                                            onChange={(e) => setFormData({ ...formData, voiceId: e.target.value })}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* [deepgram-nova-3-upgrade] Model Selector */}
                            {formData.sttProvider === 'deepgram' && formData.provider === 'internal' && (
                                <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--success-soft)', borderRadius: '0.5rem', border: '1px solid var(--success-border)' }}>
                                    <Label>Deepgram Model Tier</Label>
                                    <Select
                                        value={formData.deepgramModel || 'nova-3'}
                                        onValueChange={(v) => setFormData({ ...formData, deepgramModel: v })}
                                    >
                                        <SelectTrigger style={{ backgroundColor: 'var(--surface-raised)' }}>
                                            <SelectValue placeholder="Select Model" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="nova-3">Nova-3 Multi (Runglish + Keyterms) 🚀</SelectItem>
                                            <SelectItem value="nova-2">Nova-2 (Legacy Fallback)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <p style={{ fontSize: '0.75rem', color: 'var(--success)', marginTop: '0.25rem' }}>
                                        Nova-3 supports real-time code-switching (RU/EN) and advanced keyterm prompting.
                                    </p>
                                </div>
                            )}
                        </div>
                    </Card>
                </TabsContent>

                <TabsContent value="guardrails" style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <Card style={{ padding: '1.5rem' }}>
                        {/* Simplified Guardrails for brevity in this replace, ensuring core is there */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Label>Enable Tools</Label>
                                <Switch
                                    checked={formData.guardrails.enableTools}
                                    onCheckedChange={(c) => setFormData({ ...formData, guardrails: { ...formData.guardrails, enableTools: c } })}
                                />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Label>Transfer on Angry</Label>
                                <Switch
                                    checked={formData.guardrails.transferOnAngry}
                                    onCheckedChange={(c) => setFormData({ ...formData, guardrails: { ...formData.guardrails, transferOnAngry: c } })}
                                />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Label>Require Consent</Label>
                                <Switch
                                    checked={formData.guardrails.requireConsent}
                                    onCheckedChange={(c) => setFormData({ ...formData, guardrails: { ...formData.guardrails, requireConsent: c } })}
                                />
                            </div>
                            <div>
                                <Label>Max Duration</Label>
                                <Input type="number" value={formData.guardrails.maxCallDuration} onChange={(e) => setFormData({ ...formData, guardrails: { ...formData.guardrails, maxCallDuration: parseInt(e.target.value) } })} />
                            </div>
                        </div>
                    </Card>
                </TabsContent>
            </Tabs>

            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 mt-6">
                <Button
                    type="button"
                    variant="outline"
                    onClick={handleTestCall}
                    style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                >
                    <Play size={16} style={{ marginRight: '0.5rem' }} /> Test Configuration
                </Button>
                <div className="flex items-center gap-3">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={refetchConfig}
                        style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
                    >
                        <RefreshCw size={16} style={{ marginRight: '0.5rem' }} /> Reset
                    </Button>
                    <Button
                        type="submit"
                        disabled={loading || saving}
                        style={{
                            backgroundColor: 'var(--primary)',
                            color: 'var(--on-primary)',
                            border: '1px solid var(--accent)',
                            fontWeight: '600'
                        }}
                    >
                        {saving ? (
                            <>Saving...</>
                        ) : (
                            <><Save size={16} style={{ marginRight: '0.5rem' }} /> Save Configuration</>
                        )}
                    </Button>
                </div>
            </div>
        </form>
    );
};

export default VoiceConfigPanel;
